import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { ObjectTypes } from '../enums';

/**
 * Manages application settings and configuration
 */
export class SettingsState extends BaseState {
  // RxJS subjects for reactive state management
  public settingsChanged$ = new Subject<any>();

  private state = {
    // Canvas settings
    tolerance: 20,
    tripLen: 2000,
    aspectRatio: 1,
    textOffset: 6,
    canvasWidth: null as number | null,
    canvasHeight: null as number | null,
    webglContext: null as WebGLRenderingContext | null,
    textContext: null as CanvasRenderingContext2D | null,
    tooltipBottomOffset: 25,

    // Guide settings
    guideColor: [1, 0, 1, 0.75] as number[],

    // Block settings
    blockColor: [.1, .1, .1, 1] as number[],
    blockThickness: 2,
    connectionPointRadius: 7.5,
    connectionPointColor: [.5, .5, 1, 1] as number[],
    connectionPointThickness: 1,

    // Elbow settings
    elbowPointRadius: 5,
    elbowPointColor: [0.996, 0.918, 0, 1] as number[],

    // Tee settings
    teeColor: [0.5, 0, 0.5, 1] as number[],
    teeType: ObjectTypes.simpleTee,

    // Elbow settings
    elbowColor: [.5, 0, .5, 1] as number[],
    elbowType: ObjectTypes.simpleElbow,

    // Calculation settings
    roundTolerance: 1 ** 10 ^ -10,
    eqRoughness: 0.1,
    airTemperature: 20,
    format: null as string | number[] | null,
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.settingsChanged$.next(this.state);
  }

  // Canvas settings
  getTolerance(): number {
    return this.state.tolerance;
  }

  setTolerance(tolerance: number) {
    this.state.tolerance = tolerance;
    this.emitStateChange({ tolerance });
  }

  getTripLen(): number {
    return this.state.tripLen;
  }

  setTripLen(tripLen: number) {
    this.state.tripLen = tripLen;
    this.emitStateChange({ tripLen });
  }

  getAspectRatio(): number {
    return this.state.aspectRatio;
  }

  setAspectRatio(ratio: number) {
    this.state.aspectRatio = ratio;
    this.emitStateChange({ aspectRatio: ratio });
  }

  getTextOffset(): number {
    return this.state.textOffset;
  }

  setTextOffset(offset: number) {
    this.state.textOffset = offset;
    this.emitStateChange({ textOffset: offset });
  }

  getCanvasSize(): { width: number | null, height: number | null } {
    return {
      width: this.state.canvasWidth,
      height: this.state.canvasHeight
    };
  }

  setCanvasSize(width: number, height: number) {
    this.state.canvasWidth = width;
    this.state.canvasHeight = height;
    this.emitStateChange({ canvasWidth: width, canvasHeight: height });
  }

  getWebGLContext(): WebGLRenderingContext | null {
    return this.state.webglContext;
  }

  setWebGLContext(context: WebGLRenderingContext) {
    this.state.webglContext = context;
    this.emitStateChange({ webglContext: context });
  }

  getTextContext(): CanvasRenderingContext2D | null {
    return this.state.textContext;
  }

  setTextContext(context: CanvasRenderingContext2D) {
    this.state.textContext = context;
    this.emitStateChange({ textContext: context });
  }

  getTooltipBottomOffset(): number {
    return this.state.tooltipBottomOffset;
  }

  setTooltipBottomOffset(offset: number) {
    this.state.tooltipBottomOffset = offset;
    this.emitStateChange({ tooltipBottomOffset: offset });
  }

  // Guide settings
  getGuideColor(): number[] {
    return [...this.state.guideColor];
  }

  setGuideColor(color: number[]) {
    this.state.guideColor = [...color];
    this.emitStateChange({ guideColor: this.state.guideColor });
  }

  // Block settings
  getBlockColor(): number[] {
    return [...this.state.blockColor];
  }

  setBlockColor(color: number[]) {
    this.state.blockColor = [...color];
    this.emitStateChange({ blockColor: this.state.blockColor });
  }

  getBlockThickness(): number {
    return this.state.blockThickness;
  }

  setBlockThickness(thickness: number) {
    this.state.blockThickness = thickness;
    this.emitStateChange({ blockThickness: thickness });
  }

  getConnectionPointRadius(): number {
    return this.state.connectionPointRadius;
  }

  setConnectionPointRadius(radius: number) {
    this.state.connectionPointRadius = radius;
    this.emitStateChange({ connectionPointRadius: radius });
  }

  getConnectionPointColor(): number[] {
    return [...this.state.connectionPointColor];
  }

  setConnectionPointColor(color: number[]) {
    this.state.connectionPointColor = [...color];
    this.emitStateChange({ connectionPointColor: this.state.connectionPointColor });
  }

  getConnectionPointThickness(): number {
    return this.state.connectionPointThickness;
  }

  setConnectionPointThickness(thickness: number) {
    this.state.connectionPointThickness = thickness;
    this.emitStateChange({ connectionPointThickness: thickness });
  }

  // Elbow settings
  getElbowPointRadius(): number {
    return this.state.elbowPointRadius;
  }

  setElbowPointRadius(radius: number) {
    this.state.elbowPointRadius = radius;
    this.emitStateChange({ elbowPointRadius: radius });
  }

  getElbowPointColor(): number[] {
    return [...this.state.elbowPointColor];
  }

  setElbowPointColor(color: number[]) {
    this.state.elbowPointColor = [...color];
    this.emitStateChange({ elbowPointColor: this.state.elbowPointColor });
  }

  // Tee settings
  getTeeColor(): number[] {
    return [...this.state.teeColor];
  }

  setTeeColor(color: number[]) {
    this.state.teeColor = [...color];
    this.emitStateChange({ teeColor: this.state.teeColor });
  }

  getTeeType(): string {
    return this.state.teeType;
  }

  setTeeType(type: string) {
    this.state.teeType = type;
    this.emitStateChange({ teeType: type });
  }

  // Elbow settings
  getElbowColor(): number[] {
    return [...this.state.elbowColor];
  }

  setElbowColor(color: number[]) {
    this.state.elbowColor = [...color];
    this.emitStateChange({ elbowColor: this.state.elbowColor });
  }

  getElbowType(): string {
    return this.state.elbowType;
  }

  setElbowType(type: string) {
    this.state.elbowType = type;
    this.emitStateChange({ elbowType: type });
  }

  // Calculation settings
  getRoundTolerance(): number {
    return this.state.roundTolerance;
  }

  setRoundTolerance(tolerance: number) {
    this.state.roundTolerance = tolerance;
    this.emitStateChange({ roundTolerance: tolerance });
  }

  getEqRoughness(): number {
    return this.state.eqRoughness;
  }

  setEqRoughness(roughness: number) {
    this.state.eqRoughness = roughness;
    this.emitStateChange({ eqRoughness: roughness });
  }

  getAirTemperature(): number {
    return this.state.airTemperature;
  }

  setAirTemperature(temperature: number) {
    this.state.airTemperature = temperature;
    this.emitStateChange({ airTemperature: temperature });
  }

  getFormat(): string | number[] | null {
    return this.state.format;
  }

  setFormat(format: string | number[]) {
    this.state.format = format;
    this.emitStateChange({ format });
  }
}
